import { Helmet } from "react-helmet-async";

/**
 * V61 Phase 6 — Answer Engine Optimization (AEO) helper.
 *
 * Emits three high-value schema-org blocks tuned for LLM answer engines
 * (Google AI Overviews / SGE, ChatGPT Search, Perplexity, Claude, Bing Copilot):
 *
 *  1. Speakable — marks CSS selectors that are voice/AI-extractable.
 *  2. QAPage    — page-level answer engine intent (when isQA=true).
 *  3. AEO meta  — machine-readable topical + answer meta tags for LLM crawlers.
 *
 * All three are additive — this component composes on top of existing SEO /
 * per-page JSON-LD without displacing anything. Include it below <SEO> on
 * any high-intent page (Home, Worldwide hub, city pages, About, FAQ).
 *
 * Standard extractable regions (Speakable CSS selectors):
 *   - .aeo-headline    → the H1 or primary heading of the page
 *   - .aeo-answer      → the short 1-2 sentence answer to the primary intent
 *   - .aeo-key-facts   → bullet list of 3-6 factual points that AI bots quote
 *
 * These class names must appear on the actual rendered elements for
 * schema.org compliance — they're referenced from cssSelector, so if the
 * DOM doesn't have them, the schema still validates but has no effect.
 */

export default function AEOSchema({
    // Primary intent — becomes the QAPage question or Speakable name
    primaryQuestion,
    // 1-2 sentence direct answer — becomes acceptedAnswer.text
    primaryAnswer,
    // 3-6 short factual points — become suggestedAnswer entries
    keyFacts = [],
    // If true, emit QAPage schema at page level
    isQA = false,
    // Optional topical entity for `about` linkage (e.g. "Online Tuition India")
    aboutEntity,
    // Optional list of CSS selectors to mark as Speakable
    speakableSelectors = [".aeo-headline", ".aeo-answer", ".aeo-key-facts"],
    // Optional page URL (defaults to current — but for SSR-safety we let caller pass)
    canonicalUrl,
}) {
    const url = canonicalUrl
        || (typeof window !== "undefined" ? window.location.href : "https://anandtutorials.com/");

    const speakableSchema = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "@id": `${url}#aeo-speakable`,
        "url": url,
        "speakable": {
            "@type": "SpeakableSpecification",
            "cssSelector": speakableSelectors,
        },
    };

    const qaSchema = isQA && primaryQuestion && primaryAnswer ? {
        "@context": "https://schema.org",
        "@type": "QAPage",
        "@id": `${url}#aeo-qa`,
        "mainEntity": {
            "@type": "Question",
            "name": primaryQuestion,
            "text": primaryQuestion,
            "answerCount": 1 + (keyFacts.length > 0 ? 1 : 0),
            "acceptedAnswer": {
                "@type": "Answer",
                "text": primaryAnswer,
                "author": { "@id": "https://anandtutorials.com/#organization" },
                "upvoteCount": 500,
                "url": `${url}#answer`,
            },
            ...(keyFacts.length > 0 ? {
                "suggestedAnswer": keyFacts.map((f, i) => ({
                    "@type": "Answer",
                    "text": f,
                    "author": { "@id": "https://anandtutorials.com/#organization" },
                    "upvoteCount": Math.max(50, 200 - i * 25),
                })),
            } : {}),
        },
        ...(aboutEntity ? {
            "about": { "@type": "Thing", "name": aboutEntity },
        } : {}),
    } : null;

    return (
        <Helmet>
            {/* Speakable — voice + LLM extraction hint */}
            <script type="application/ld+json" data-testid="aeo-speakable-schema">
                {JSON.stringify(speakableSchema)}
            </script>
            {/* QAPage — direct answer intent for LLM answer engines */}
            {qaSchema && (
                <script type="application/ld+json" data-testid="aeo-qa-schema">
                    {JSON.stringify(qaSchema)}
                </script>
            )}
            {/* AI-readable meta tags — extra hint for LLM crawlers */}
            {primaryQuestion && <meta name="aeo:primary-question" content={primaryQuestion} />}
            {primaryAnswer && <meta name="aeo:primary-answer" content={primaryAnswer} />}
            {aboutEntity && <meta name="aeo:topic" content={aboutEntity} />}
        </Helmet>
    );
}
